import type { WidgetConfig } from '../theme.js';
import { useWidgetData } from '../api.js';
import { EmptyState, ErrorState, Frame, Loading, UnavailableState } from '../chrome.js';
import { formatCompact, formatPercent, formatPrice, formatStamp } from '../format.js';

/**
 * Crypto ticker.
 *
 * A flex-wrapped card grid rather than a table: this is the widget most likely
 * to be dropped into a narrow sidebar or a wide hero strip, and cards reflow
 * gracefully between the two where table columns do not. The parent is told the
 * resulting height either way.
 */

interface CryptoQuote {
  symbol: string;
  name: string | null;
  quote_currency: string;
  price: number;
  change_24h_pct: number | null;
  market_cap: number | null;
  volume_24h: number | null;
  as_of: string;
}

const TITLE = 'Crypto prices';

export function CryptoTicker({ config }: { config: WidgetConfig }) {
  const state = useWidgetData<CryptoQuote[]>(config, '/v1/crypto');

  if (state.status === 'loading') return <Loading title={TITLE} rows={5} />;
  if (state.status === 'error') return <ErrorState title={TITLE} message={state.message} />;
  if (state.status === 'unavailable')
    return (
      <UnavailableState
        title={TITLE}
        message={state.message}
        lastGoodAt={state.lastGoodAt}
        locale={config.locale}
        timeZone={config.timeZone}
      />
    );

  const { envelope, stale } = state;

  return (
    <Frame
      title={TITLE}
      stamp={envelope.as_of ? formatStamp(envelope.as_of, config.locale, config.timeZone) : undefined}
      stale={stale}
      attributions={envelope.attributions}
      locale={config.locale}
    >
      {envelope.data.length === 0 ? (
        <EmptyState message="No prices are available right now." />
      ) : (
        <div className="ticker">
          {envelope.data.map((coin) => {
            const change = coin.change_24h_pct;
            // null and 0 are different things: the CC0 standby provider does not
            // publish a 24h change at all, and rendering that as "0.00%" would
            // assert something we do not know.
            const direction = change === null ? null : change >= 0 ? 'up' : 'down';

            return (
              <div className="ticker-item" key={coin.symbol}>
                <div className="ticker-sym">
                  <span className="sym">{coin.symbol}</span>
                  {coin.name && <span className="ticker-name">{coin.name}</span>}
                </div>

                <div className="ticker-price">
                  {formatPrice(coin.price, coin.quote_currency, config.locale)}
                </div>

                <div style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  {change === null ? (
                    <span className="muted" style={{ fontSize: 'var(--font-size-sm)' }}>
                      24h n/a
                    </span>
                  ) : (
                    <span className={`pill ${direction}`}>
                      {formatPercent(change, config.locale)}%
                    </span>
                  )}
                  {coin.market_cap !== null && (
                    <span className="muted" style={{ fontSize: 'var(--font-size-sm)' }}>
                      cap {formatCompact(coin.market_cap, config.locale)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Frame>
  );
}
